import {
  CustomField,
  updatePhoneValueForm,
  updatePhoneValueSearch,
} from '@sales-center/common'
import { DraftLead } from '@user/store/draftLeads/typings'
import { IServiceLocation } from '@user/store/locations/typings'
import { ITargetPest, pestNamesToIds } from '@user/store/targetPests'
import { ILeadFormModel, createInitialValues } from './typings'

const getDraftInitialValues = (
  customFields: CustomField[],
  draft: DraftLead,
  allTargetPests: ITargetPest[],
  serviceLocation?: IServiceLocation,
): ILeadFormModel => {
  const targetPestInput: string[] = pestNamesToIds(
    allTargetPests,
    draft.targetPests,
  )
  if (serviceLocation) {
    return {
      ...getInitialValuesForLocation(customFields, serviceLocation),
      draftLeadId: draft.id,
      type: draft.type,
      draftNote: draft.note || '',
      title: draft.title || '',
      submittedBy: draft.userName,
      submittedDate: draft.createdTime,
      importId: draft.importId,
      importName: draft.importName,
      altPhoneNumber:
        draft.type === 'Mobile' && draft.mobilePhone
          ? updatePhoneValueSearch(draft.mobilePhone)
          : serviceLocation.altPhone
          ? serviceLocation.altPhone
          : '',
      altPhoneExt:
        draft.type === 'Mobile' && draft.mobilePhone
          ? ''
          : serviceLocation.altPhoneExt
          ? serviceLocation.altPhoneExt
          : '',
      referral:
        draft.referringBillTo === null
          ? null
          : {
              referralSource: '',
              referralValue: draft.referringBillTo,
              referralDiscountCode: draft.referralCode,
              referralDiscountId: draft.discountCodeId,
              referralDiscountValue: 0.0,
              referralType: 'Customer',
              referralLocationCode: '',
              referralEmployeeId:  null,
            },
      targetPests: targetPestInput,
      salesTeamId: draft.salesTeamId === null ? '' : draft.salesTeamId,
      salesFunnelId: draft.salesFunnelId === null ? '' : draft.salesFunnelId,
      assigneeId: draft.assigneeId === null ? '' : draft.assigneeId,
      campaignId: draft.campaignId || '',
      leadSourceId: draft.sourceId || '',
      campaign: draft.campaign || '',
      leadSource: draft.source || '',
    }
  } else {
    const emailsSplit = draft?.emailAddress
      ? draft.emailAddress.includes(',')
        ? draft?.emailAddress.split(',')
        : draft?.emailAddress.split(';')
      : null

    const usableEmails = emailsSplit?.splice(0, 3)

    return {
      ...createInitialValues(customFields, draft.customFieldValues),
      address1: draft.street1 || '',
      address2: draft.street2 || '',
      altPhoneExt:
        draft.type === 'Mobile' && draft.mobilePhone
          ? ''
          : draft.altPhoneExt
          ? draft.altPhoneExt
          : '',
      altPhoneNumber:
        draft.type === 'Mobile' && draft.mobilePhone
          ? updatePhoneValueSearch(draft.mobilePhone)
          : draft.altPhoneNumber
          ? updatePhoneValueSearch(draft.altPhoneNumber)
          : '',
      branch: draft.branch || '',
      branchId: draft.branchId || '',
      campaignId: draft.campaignId || '',
      city: draft.city || '',
      country: draft.country || '',
      company: draft.company || '',
      draftLeadId: draft.id,
      type:
        draft.type === null || draft.type.length === 0 ? 'Manual' : draft.type,
      firstName: draft.firstName || '',
      lastName: draft.lastName || '',
      leadSourceId: draft.sourceId || '',
      lat: draft.latitude || 0,
      locationType: draft.locationType || '',
      locationTypeId: draft.locationTypeId || '',
      lng: draft.longitude || 0,
      note: '',
      draftNote: draft.note || '',
      importId: draft.importId,
      importName: draft.importName,
      phoneExt: draft.phoneExt || '',
      phoneNumber: draft.phoneNumber
        ? updatePhoneValueSearch(draft.phoneNumber)
        : '',
      emailAddresses: usableEmails
        ? usableEmails.map((e, index) => ({
            emailAddress: e,
            isPrimary: index === 0,
          }))
        : [{ emailAddress: '', isPrimary: true }],
      postalCode: draft.postalCode || '',
      state: draft.stateAbbreviation || '',
      title: draft.title || '',
      externalLocationId: draft.locationId || '',
      submittedBy: draft.userName,
      submittedDate: draft.createdTime,
      referral:
        draft.referringBillTo === null
          ? null
          : {
              referralSource: '',
              referralValue: draft.referringBillTo,
              referralDiscountCode: draft.referralCode,
              referralDiscountId: draft.discountCodeId,
              referralDiscountValue: 0.0,
              referralType: 'Customer',
              referralLocationCode: '',
              referralEmployeeId:  null,
            },
      targetPests: targetPestInput,
      salesTeamId: draft.salesTeamId === null ? '' : draft.salesTeamId,
      salesFunnelId: draft.salesFunnelId === null ? '' : draft.salesFunnelId,
      assigneeId: draft.assigneeId === null ? '' : draft.assigneeId,
      campaign: draft.campaign || '',
      leadSource: draft.source || '',
      webFormCallTrackingEventId: draft.webFormCallTrackingEventId,
    }
  }
}

export const getInitialValues = (
  customFields: CustomField[],
  draft: DraftLead | undefined,
  allTargetPests: ITargetPest[],
  serviceLocation?: IServiceLocation,
  phone?: string | null,
): ILeadFormModel => {
  if (draft) {
    return getDraftInitialValues(
      customFields,
      draft,
      allTargetPests,
      serviceLocation,
    )
  } else {
    return createInitialValues(customFields, [], phone)
  }
}

export const getInitialValuesForLocation = (
  customFields: CustomField[],
  location: IServiceLocation | undefined,
  phone?: string | null,
): ILeadFormModel => {
  const initialValues = createInitialValues(customFields, [], phone)

  if (location) {
    const address = location?.address
    const emailsSplit = location.email.includes(',')
      ? location?.email.split(',')
      : location?.email.split(';')

    const usableEmails = emailsSplit.splice(0, 3)
    const extraEmails = emailsSplit.splice(4).join(', ')

    return {
      ...initialValues,
      externalLocationId: location.id || '',
      externalLocationCode: location.number || '',
      address1: address.address || '',
      address2: address.address2 || '',
      city: address.city || '',
      state: address.state || '',
      country: address.country || '',
      postalCode: address.postalCode || '',

      lat: address.lat || 0,
      lng: address.lng || 0,

      altPhoneExt: location.altPhoneExt || '',
      altPhoneNumber: location.altPhone || '',
      phoneNumber:
        formatInitialPhoneNumberValue(location.phone) || (phone ?? ''),
      emailAddresses: usableEmails
        ? usableEmails.map((e, index) => ({
            emailAddress: e,
            isPrimary: index === 0,
          }))
        : [{ emailAddress: '', isPrimary: true }],

      firstName: location.firstName || '',
      lastName: location.lastName || '',
      company: location.company || '',

      campaignId: location.campaignId || '',
      campaign: location.campaign || '',

      leadSourceId: location.sourceId || '',
      leadSource: location.source || '',

      divisionId: location.divisionId || '',
      division: location.division || '',

      locationType: location.locationType || '',
      locationTypeId: location.locationTypeId || '',

      branchId: location.branchId || '',
      branch: location.branch || '',
      phoneExt: location.phoneExt || '',
      note: extraEmails,
    }
  } else {
    return initialValues
  }
}

export const formatInitialPhoneNumberValue = (phoneNumber?: string | null) => {
  if (phoneNumber) {
    let match = phoneNumber.replace(/\D/g, '')
    match = match.length > 10 ? match.slice(-10) : match
    return updatePhoneValueForm(match, false)
  }
  return phoneNumber
}

export const formatPhoneNumber = (phoneNumber: string) => {
  const match = ('' + phoneNumber)
    .replace(/\D/g, '')
    .match(/^(\d{3})(\d{3})(\d{4})$/)

  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3]
  }

  return phoneNumber
}
